import { Assets, Graphics, Spritesheet, Text, loadTextures, type Renderer, type Texture } from 'pixi.js';
import { ASSETS } from '../../config.ts';
import { PALETTE } from '../palette.ts';
import { PLACEHOLDER_ALIASES } from '../placeholderAliases.ts';

/**
 * Atlas contract. WP1 ships a procedurally generated placeholder atlas; WP4 replaces it with
 * SVG-sourced spritesheets exported at 1x/2x/3x, loaded through the same interface.
 */
export interface SpriteAtlas {
  /** Texture by sprite name. Throws on unknown names so typos fail loudly. */
  get(name: string): Texture;
  has(name: string): boolean;
  /** Ordered frames for an animation, e.g. 'pip.dig' → [pip.dig.0, pip.dig.1, …]. */
  frames(prefix: string): Texture[];
}

export class MapAtlas implements SpriteAtlas {
  private readonly map: Map<string, Texture>;
  constructor(map: Map<string, Texture>) {
    this.map = map;
  }
  get(name: string): Texture {
    const t = this.map.get(name);
    if (!t) throw new Error(`Atlas: no sprite named '${name}'`);
    return t;
  }
  has(name: string): boolean {
    return this.map.has(name);
  }
  frames(prefix: string): Texture[] {
    const out: [number, Texture][] = [];
    for (const [k, v] of this.map) {
      if (k.startsWith(prefix + '.')) {
        const n = Number(k.slice(prefix.length + 1));
        if (!Number.isNaN(n)) out.push([n, v]);
      }
    }
    return out.sort((a, b) => a[0] - b[0]).map((x) => x[1]);
  }
}

/**
 * Load sliced art from `public/art/<scale>x/<name>.png` as listed in `public/art/index.json`
 * (written by `npm run art`). Textures are tagged with their scale so a 128 px sprite measures
 * 64 logical px (one cell) in the scene. Names that fail to load are simply absent.
 */
export interface ArtLoadReport {
  requested: number;
  loaded: number;
  failed: string[];
  firstError: string | null;
  ms: number;
  source: 'inline' | 'folder' | 'none';
}

export async function loadFolderAtlas(
  base: string,
  scale: number,
  inline?: Record<string, string>,
  onProgress?: (loaded: number, total: number) => void,
  report?: ArtLoadReport,
): Promise<SpriteAtlas> {
  // Decode through <img> elements rather than worker + createImageBitmap: the worker path is the
  // one that fails silently on iOS Safari for data: URIs, and <img> handles both paths everywhere.
  if (loadTextures.config) {
    loadTextures.config.preferWorkers = false;
    loadTextures.config.preferCreateImageBitmap = false;
  }
  const t0 = performance.now();
  const map = new Map<string, Texture>();
  let names: string[] = [];
  if (inline) names = Object.keys(inline);
  else {
    try {
      const res = await fetch(`${base}/index.json`);
      if (res.ok) names = ((await res.json()) as { names: string[] }).names;
    } catch {
      /* no art folder: empty atlas */
    }
  }
  let done = 0;
  const failed: string[] = [];
  let firstError: string | null = null;
  await Promise.all(
    names.map(async (name) => {
      try {
        const src = inline ? inline[name] : `${base}/${scale}x/${name}.png`;
        const tex = (await Assets.load({ alias: `art:${name}`, src, data: { resolution: scale }, parser: 'loadTextures' })) as Texture;
        map.set(name, tex);
      } catch (e) {
        failed.push(name);
        if (!firstError) firstError = e instanceof Error ? e.message : String(e);
      }
      done++;
      onProgress?.(done, names.length);
    }),
  );
  if (report) {
    report.requested = names.length;
    report.loaded = map.size;
    report.failed = failed;
    report.firstError = firstError;
    report.ms = Math.round(performance.now() - t0);
    report.source = names.length === 0 ? 'none' : inline ? 'inline' : 'folder';
  }
  return new MapAtlas(map);
}

/** Real art over placeholders: every ASSETS name resolves, real names win. */
export class LayeredAtlas implements SpriteAtlas {
  private readonly top: SpriteAtlas;
  private readonly base: SpriteAtlas;
  constructor(top: SpriteAtlas, base: SpriteAtlas) {
    this.top = top;
    this.base = base;
  }
  get(name: string): Texture {
    return this.top.has(name) ? this.top.get(name) : this.base.get(name);
  }
  has(name: string): boolean {
    return this.top.has(name) || this.base.has(name);
  }
  frames(prefix: string): Texture[] {
    const t = this.top.frames(prefix);
    return t.length ? t : this.base.frames(prefix);
  }
  /** True when the name comes from the real-art layer. */
  isReal(name: string): boolean {
    return this.top.has(name);
  }
}

/** Load a Pixi spritesheet JSON (TexturePacker-style) and expose it through SpriteAtlas. */
export async function loadSpritesheetAtlas(url: string): Promise<SpriteAtlas> {
  const sheet = (await Assets.load(url)) as Spritesheet;
  const map = new Map<string, Texture>();
  for (const [name, tex] of Object.entries(sheet.textures)) map.set(name.replace(/\.(png|webp)$/, ''), tex);
  return new MapAtlas(map);
}

/** Base pixel size of one cell in generated textures. Sprites are scaled to the live cell size. */
export const TEX_CELL = 64;

/**
 * Placeholder atlas: flat, rounded shapes in the §1 palette, drawn with Graphics and baked to
 * textures once. Every sprite is TEX_CELL px per cell unit, anchored at centre.
 */
export function buildPlaceholderAtlas(renderer: Renderer): SpriteAtlas {
  const map = new Map<string, Texture>();
  const C = TEX_CELL;
  const bake = (name: string, draw: (g: Graphics) => void, size = C, height = size): void => {
    const g = new Graphics();
    // Transparent bounds so every texture has a predictable size and anchor.
    g.rect(0, 0, size, height).fill({ color: 0, alpha: 0 });
    draw(g);
    map.set(name, renderer.generateTexture({ target: g, resolution: 2 }));
    g.destroy();
  };

  // Tiles
  bake('tile.dirt', (g) => {
    g.rect(0, 0, C, C).fill(PALETTE.earth);
    g.circle(C * 0.28, C * 0.32, C * 0.06).fill(PALETTE.earthSpeck);
    g.circle(C * 0.7, C * 0.62, C * 0.05).fill(PALETTE.earthSpeck);
    g.circle(C * 0.5, C * 0.82, C * 0.04).fill(PALETTE.earthDark);
  });
  bake(ASSETS.earthMaterial, (g) => {
    g.rect(0, 0, C * 4, C * 4).fill(PALETTE.earth);
    for (let i = 0; i < 90; i++) g.circle((((i * 97) % 256) / 256) * C * 4, (((i * 57) % 256) / 256) * C * 4, C * 0.05).fill(i % 3 ? PALETTE.earthSpeck : PALETTE.earthDark);
  }, C * 4, C * 4);
  for (let i = 0; i < 16; i++) {
    bake(`tile.earth.${i}`, (g) => {
      g.rect(0, 0, C, C).fill(i % 2 ? PALETTE.earth : PALETTE.earthMid);
      g.circle(C * (0.2 + (i % 4) * 0.18), C * (0.25 + Math.floor(i / 4) * 0.16), C * 0.05).fill(PALETTE.earthSpeck);
      g.circle(C * (0.75 - (i % 3) * 0.12), C * (0.7 - (i % 5) * 0.08), C * 0.04).fill(PALETTE.earthDark);
    });
  }
  bake('tile.tunnel.rim', (g) => {
    g.roundRect(0, 0, C, C, C * 0.25).fill(PALETTE.tunnel);
  });
  bake('tile.tunnel.end', (g) => {
    g.roundRect(0, 0, C, C, C * 0.4).fill(PALETTE.tunnel);
  });
  bake('tile.tunnel.lit', (g) => {
    g.rect(0, 0, C, C).fill(PALETTE.tunnelGlow);
  });
  bake('tile.dirt2', (g) => {
    g.rect(0, 0, C, C).fill(PALETTE.earth);
    g.circle(C * 0.62, C * 0.25, C * 0.05).fill(PALETTE.earthSpeck);
    g.circle(C * 0.25, C * 0.7, C * 0.06).fill(PALETTE.earthDark);
  });
  bake('tile.tunnel', (g) => {
    g.rect(0, 0, C, C).fill(PALETTE.tunnel);
    g.roundRect(C * 0.12, C * 0.12, C * 0.76, C * 0.76, C * 0.2).fill({ color: PALETTE.tunnelGlow, alpha: 0.35 });
  });

  // Gems: five jewel types
  PALETTE.gems.forEach((color, i) => {
    bake(`gem.${i}`, (g) => {
      const cx = C / 2;
      const cy = C / 2;
      const r = C * 0.3;
      g.poly([cx, cy - r, cx + r * 0.8, cy, cx, cy + r, cx - r * 0.8, cy]).fill(color);
      g.poly([cx, cy, cx + r * 0.8, cy, cx, cy + r]).fill({ color: 0x000000, alpha: 0.22 });
      g.circle(cx - r * 0.25, cy - r * 0.35, r * 0.16).fill(PALETTE.gemSparkle);
    });
  });

  // Sack
  bake('sack', (g) => {
    g.moveTo(C * 0.2, C * 0.9)
      .quadraticCurveTo(C * 0.02, C * 0.45, C * 0.36, C * 0.26)
      .lineTo(C * 0.64, C * 0.26)
      .quadraticCurveTo(C * 0.98, C * 0.45, C * 0.8, C * 0.9)
      .quadraticCurveTo(C * 0.5, C * 0.98, C * 0.2, C * 0.9)
      .closePath()
      .fill(PALETTE.sack);
    g.ellipse(C * 0.5, C * 0.86, C * 0.28, C * 0.07).fill({ color: PALETTE.sackShade, alpha: 0.7 });
    g.roundRect(C * 0.34, C * 0.14, C * 0.32, C * 0.16, C * 0.06).fill(PALETTE.sackTie);
  });
  bake('sack.shadow', (g) => {
    g.ellipse(C / 2, C * 0.8, C * 0.36, C * 0.12).fill({ color: 0x000000, alpha: 0.35 });
  });

  // Gold pile + coin particle
  bake('gold', (g) => {
    g.ellipse(C * 0.5, C * 0.7, C * 0.34, C * 0.16).fill(PALETTE.goldShade);
    g.ellipse(C * 0.5, C * 0.64, C * 0.34, C * 0.16).fill(PALETTE.gold);
    g.ellipse(C * 0.5, C * 0.48, C * 0.22, C * 0.12).fill(PALETTE.gold);
    g.ellipse(C * 0.42, C * 0.45, C * 0.06, C * 0.03).fill(0xffffff);
  });
  bake('coin', (g) => {
    g.circle(C / 2, C / 2, C * 0.12).fill(PALETTE.gold);
    g.circle(C / 2, C / 2, C * 0.07).stroke({ color: PALETTE.goldShade, width: C * 0.02 });
  });

  // Particles
  bake('dust', (g) => {
    g.circle(C / 2, C / 2, C * 0.1).fill(0xffffff);
  });
  bake('star', (g) => {
    const cx = C / 2;
    const cy = C / 2;
    const pts: number[] = [];
    for (let i = 0; i < 10; i++) {
      const rr = i % 2 === 0 ? C * 0.16 : C * 0.07;
      const a = (i * Math.PI) / 5 - Math.PI / 2;
      pts.push(cx + Math.cos(a) * rr, cy + Math.sin(a) * rr);
    }
    g.poly(pts).fill(0xffffff);
  });

  // Bonus fruit (cherry-like, original shape)
  bake('bonus', (g) => {
    const cx = C / 2;
    g.circle(cx - C * 0.14, C * 0.62, C * 0.2).fill(PALETTE.bonus);
    g.circle(cx + C * 0.16, C * 0.66, C * 0.18).fill(PALETTE.bonus);
    g.moveTo(cx - C * 0.14, C * 0.44).quadraticCurveTo(cx, C * 0.1, cx + C * 0.2, C * 0.2).stroke({ color: 0x4a7a2a, width: C * 0.05 });
    g.moveTo(cx + C * 0.16, C * 0.5).quadraticCurveTo(cx + C * 0.1, C * 0.25, cx + C * 0.2, C * 0.2).stroke({ color: 0x4a7a2a, width: C * 0.05 });
    g.circle(cx - C * 0.22, C * 0.55, C * 0.05).fill(0xffffff);
  });

  // Pip (miner): round body, oversized helmet with lamp. Facing right; flipped/rotated in scene.
  bake('pip', (g) => {
    const cx = C / 2;
    const cy = C / 2;
    g.circle(cx, cy + C * 0.06, C * 0.3).fill(PALETTE.pipBody);
    g.circle(cx, cy - C * 0.1, C * 0.3).fill(PALETTE.pip);
    g.rect(cx - C * 0.34, cy - C * 0.12, C * 0.68, C * 0.08).fill(PALETTE.pip);
    g.circle(cx + C * 0.16, cy - C * 0.14, C * 0.09).fill(PALETTE.pipLamp);
    g.circle(cx + C * 0.1, cy + C * 0.04, C * 0.06).fill(0xffffff);
    g.circle(cx + C * 0.12, cy + C * 0.05, C * 0.03).fill(0x222222);
  });
  bake('pip.crushed', (g) => {
    g.ellipse(C / 2, C * 0.72, C * 0.42, C * 0.14).fill(PALETTE.pipBody);
    g.ellipse(C / 2, C * 0.6, C * 0.34, C * 0.1).fill(PALETTE.pip);
  });

  // Crawler / Borer
  bake('crawler', (g) => {
    const cx = C / 2;
    const cy = C / 2;
    g.circle(cx, cy, C * 0.34).fill(PALETTE.crawler);
    g.circle(cx - C * 0.12, cy - C * 0.06, C * 0.1).fill(0xffffff);
    g.circle(cx + C * 0.12, cy - C * 0.06, C * 0.1).fill(0xffffff);
    g.circle(cx - C * 0.1, cy - C * 0.05, C * 0.05).fill(0x222222);
    g.circle(cx + C * 0.14, cy - C * 0.05, C * 0.05).fill(0x222222);
  });
  bake('borer', (g) => {
    const cx = C / 2;
    const cy = C / 2;
    const pts: number[] = [];
    for (let i = 0; i < 16; i++) {
      const rr = i % 2 === 0 ? C * 0.4 : C * 0.3;
      const a = (i * Math.PI) / 8;
      pts.push(cx + Math.cos(a) * rr, cy + Math.sin(a) * rr);
    }
    g.poly(pts).fill(PALETTE.borer);
    g.circle(cx, cy, C * 0.24).fill(PALETTE.borerAlt);
    g.circle(cx - C * 0.1, cy - C * 0.06, C * 0.08).fill(0xffffff);
    g.circle(cx + C * 0.1, cy - C * 0.06, C * 0.08).fill(0xffffff);
    g.circle(cx - C * 0.08, cy - C * 0.05, C * 0.04).fill(0x222222);
    g.circle(cx + C * 0.12, cy - C * 0.05, C * 0.04).fill(0x222222);
  });

  // Flame streak (pointing right)
  bake('flame', (g) => {
    g.ellipse(C * 0.5, C * 0.5, C * 0.32, C * 0.16).fill(PALETTE.flame);
    g.ellipse(C * 0.56, C * 0.5, C * 0.18, C * 0.08).fill(PALETTE.flameCore);
  });

  // Arrow (pointing right)
  bake('arrow', (g) => {
    const r = C * 0.22;
    g.poly([C / 2 + r, C / 2, C / 2 - r * 0.6, C / 2 - r * 0.7, C / 2 - r * 0.6, C / 2 + r * 0.7]).fill(0xffffff);
  });

  // Soft radial glow (lamp, bonus halo): concentric discs with falling alpha, additive-blended in use.
  bake('glow', (g) => {
    for (let i = 8; i >= 1; i--) g.circle(C / 2, C / 2, (C * 0.5 * i) / 8).fill({ color: 0xffffff, alpha: 0.09 });
  });
  bake('confetti', (g) => {
    g.roundRect(C * 0.42, C * 0.36, C * 0.16, C * 0.28, C * 0.03).fill(0xffffff);
  });

  // ---- Bible §5 placeholders: lantern, flame icon, Grandpa bust, headframe, sign, floor props.
  const lantern = (g: Graphics, fill: number, flash: boolean): void => {
    // Brass lantern: cap, glass body, base; the flame regrows with `fill` (0..1).
    g.roundRect(C * 0.3, C * 0.1, C * 0.4, C * 0.1, C * 0.03).fill(PALETTE.brass);
    g.roundRect(C * 0.44, C * 0.02, C * 0.12, C * 0.1, C * 0.03).fill(PALETTE.brass);
    g.roundRect(C * 0.26, C * 0.2, C * 0.48, C * 0.58, C * 0.1).fill({ color: 0xffffff, alpha: 0.18 });
    g.roundRect(C * 0.26, C * 0.2, C * 0.48, C * 0.58, C * 0.1).stroke({ color: PALETTE.brass, width: C * 0.05 });
    g.roundRect(C * 0.22, C * 0.78, C * 0.56, C * 0.12, C * 0.04).fill(PALETTE.brass);
    if (fill > 0) {
      const h = C * 0.44 * fill;
      const cx = C * 0.5;
      const base = C * 0.72;
      g.moveTo(cx - C * 0.11, base).quadraticCurveTo(cx - C * 0.16, base - h * 0.5, cx, base - h).quadraticCurveTo(cx + C * 0.16, base - h * 0.5, cx + C * 0.11, base).closePath().fill(PALETTE.flame);
      g.moveTo(cx - C * 0.05, base).quadraticCurveTo(cx - C * 0.07, base - h * 0.35, cx, base - h * 0.6).quadraticCurveTo(cx + C * 0.07, base - h * 0.35, cx + C * 0.05, base).closePath().fill(PALETTE.flameCore);
    }
    if (flash) g.circle(C * 0.5, C * 0.5, C * 0.48).stroke({ color: 0xffffff, width: C * 0.06, alpha: 0.9 });
  };
  for (let i = 0; i < ASSETS.lanternLevels; i++) bake(ASSETS.lanternFill(i), (g) => lantern(g, i / (ASSETS.lanternLevels - 1), false));
  bake(ASSETS.lanternFlash, (g) => lantern(g, 1, true));
  for (let i = 0; i < ASSETS.flameIconLevels; i++) {
    bake(ASSETS.flameIconFill(i), (g) => {
      const f = i / (ASSETS.flameIconLevels - 1);
      g.circle(C * 0.5, C * 0.55, C * 0.3).fill({ color: PALETTE.uiFrame, alpha: 0.55 });
      g.circle(C * 0.5, C * 0.55, C * 0.3).stroke({ color: PALETTE.uiTrim, width: C * 0.04, alpha: 0.9 });
      if (f > 0) {
        const h = C * 0.42 * f;
        const cx = C * 0.5;
        const base = C * 0.76;
        g.moveTo(cx - C * 0.13, base).quadraticCurveTo(cx - C * 0.18, base - h * 0.5, cx, base - h).quadraticCurveTo(cx + C * 0.18, base - h * 0.5, cx + C * 0.13, base).closePath().fill(PALETTE.flame);
        g.moveTo(cx - C * 0.06, base).quadraticCurveTo(cx - C * 0.08, base - h * 0.35, cx, base - h * 0.6).quadraticCurveTo(cx + C * 0.08, base - h * 0.35, cx + C * 0.06, base).closePath().fill(PALETTE.flameCore);
      }
    });
  }
  // Grandpa bust: round face, enormous moustache, expression by state.
  type Face = 'neutral' | 'browsUp' | 'wince' | 'snore' | 'partyHat' | 'delighted' | 'gobsmacked';
  const grandpa = (g: Graphics, face: Face, frame = 0): void => {
    const cx = C / 2;
    const cy = C * 0.55;
    if (face === 'partyHat') {
      g.poly([cx - C * 0.16, C * 0.24, cx + C * 0.14, C * 0.24, cx + C * 0.02, C * -0.02]).fill(PALETTE.bonus);
      g.circle(cx + C * 0.02, C * 0.0, C * 0.05).fill(PALETTE.gold);
    }
    g.circle(cx, cy, C * 0.36).fill(PALETTE.skin);
    // Cap
    g.moveTo(cx - C * 0.38, cy - C * 0.12).quadraticCurveTo(cx, cy - C * 0.62, cx + C * 0.38, cy - C * 0.12).lineTo(cx + C * 0.4, cy - C * 0.08).lineTo(cx - C * 0.4, cy - C * 0.08).closePath().fill(PALETTE.wood);
    // Eyes
    const ey = cy - C * 0.05;
    if (face === 'snore') {
      g.rect(cx - C * 0.2, ey, C * 0.12, C * 0.025).fill(0x333333);
      g.rect(cx + C * 0.08, ey, C * 0.12, C * 0.025).fill(0x333333);
      const zx = cx + C * 0.3 + frame * C * 0.04;
      const zy = cy - C * 0.28 - frame * C * 0.05;
      g.rect(zx, zy, C * 0.08, C * 0.02).fill(0xffffff);
      g.rect(zx, zy + C * 0.06, C * 0.08, C * 0.02).fill(0xffffff);
    } else if (face === 'wince') {
      g.moveTo(cx - C * 0.22, ey - C * 0.03).lineTo(cx - C * 0.1, ey + C * 0.02).lineTo(cx - C * 0.22, ey + C * 0.06).stroke({ color: 0x333333, width: C * 0.03 });
      g.moveTo(cx + C * 0.22, ey - C * 0.03).lineTo(cx + C * 0.1, ey + C * 0.02).lineTo(cx + C * 0.22, ey + C * 0.06).stroke({ color: 0x333333, width: C * 0.03 });
    } else {
      const r = face === 'gobsmacked' || face === 'browsUp' ? C * 0.07 : C * 0.05;
      g.circle(cx - C * 0.14, ey, r).fill(0x333333);
      g.circle(cx + C * 0.14, ey, r).fill(0x333333);
      g.circle(cx - C * 0.12, ey - C * 0.02, r * 0.35).fill(0xffffff);
      g.circle(cx + C * 0.16, ey - C * 0.02, r * 0.35).fill(0xffffff);
    }
    // Brows
    const by = face === 'browsUp' ? ey - C * 0.18 : ey - C * 0.11;
    g.rect(cx - C * 0.22, by, C * 0.16, C * 0.035).fill(PALETTE.moustache);
    g.rect(cx + C * 0.06, by, C * 0.16, C * 0.035).fill(PALETTE.moustache);
    // Moustache (enormous)
    g.ellipse(cx - C * 0.14, cy + C * 0.12, C * 0.2, C * 0.09).fill(PALETTE.moustache);
    g.ellipse(cx + C * 0.14, cy + C * 0.12, C * 0.2, C * 0.09).fill(PALETTE.moustache);
    if (face === 'gobsmacked') g.circle(cx, cy + C * 0.25, C * 0.06).fill(0x5a2a22);
    if (face === 'delighted') g.moveTo(cx - C * 0.1, cy + C * 0.22).quadraticCurveTo(cx, cy + C * 0.32, cx + C * 0.1, cy + C * 0.22).stroke({ color: 0x5a2a22, width: C * 0.03 });
    // Lollipop
    g.rect(cx + C * 0.24, cy + C * 0.16, C * 0.02, C * 0.16).fill(0xffffff);
    g.circle(cx + C * 0.25, cy + C * 0.14, C * 0.05).fill(PALETTE.gems[0]);
  };
  bake(ASSETS.grandpa.neutral, (g) => grandpa(g, 'neutral'));
  bake(ASSETS.grandpa.browsUp, (g) => grandpa(g, 'browsUp'));
  bake(ASSETS.grandpa.wince, (g) => grandpa(g, 'wince'));
  bake(ASSETS.grandpa.partyHat, (g) => grandpa(g, 'partyHat'));
  bake(ASSETS.grandpa.delighted, (g) => grandpa(g, 'delighted'));
  bake(ASSETS.grandpa.gobsmacked, (g) => grandpa(g, 'gobsmacked'));
  for (let i = 0; i < ASSETS.grandpa.snoreFrames; i++) bake(ASSETS.grandpa.snore(i), (g) => grandpa(g, 'snore', i));

  // Headframe silhouette (A-frame legs, cross beam, sheave wheel), 3 restoration states.
  const headframe = (g: Graphics, state: 'collapsed' | 'patched' | 'restored'): void => {
    const W = C * 4;
    const H = C * 2;
    const col = PALETTE.uiFrame;
    const lean = state === 'collapsed' ? 0.12 : state === 'patched' ? 0.05 : 0;
    g.poly([W * 0.12, H, W * (0.42 + lean), H * 0.05, W * (0.5 + lean), H * 0.05, W * 0.24, H]).fill(col);
    g.poly([W * 0.88, H, W * (0.58 + lean), H * 0.05, W * (0.5 + lean), H * 0.05, W * 0.76, H]).fill(col);
    g.rect(W * 0.2, H * 0.5, W * 0.6, H * 0.06).fill(col);
    g.circle(W * (0.5 + lean), H * 0.12, H * 0.14).stroke({ color: col, width: H * 0.05 });
    if (state !== 'collapsed') g.rect(W * 0.3, H * 0.28, W * 0.4, H * 0.05).fill(col);
    if (state === 'restored') g.rect(W * 0.46 + lean * W, H * 0.02, W * 0.08, H * 0.03).fill(PALETTE.uiTrim);
  };
  bake(ASSETS.headframe.collapsed, (g) => headframe(g, 'collapsed'), C * 4, C * 2);
  bake(ASSETS.headframe.patched, (g) => headframe(g, 'patched'), C * 4, C * 2);
  bake(ASSETS.headframe.restored, (g) => headframe(g, 'restored'), C * 4, C * 2);
  // Sign plate (9-slice later): lacquered wood body with a gold trim. Baked 8:1 and stretched.
  bake(ASSETS.sign, (g) => {
    g.roundRect(0, 0, C * 8, C, C * 0.22).fill(PALETTE.uiFrame);
    g.roundRect(C * 0.08, C * 0.08, C * 7.84, C * 0.84, C * 0.18).stroke({ color: PALETTE.uiTrim, width: C * 0.06 });
  }, C * 8, C);
  bake(ASSETS.brassPlate, (g) => {
    g.roundRect(0, 0, C * 4, C * 0.6, C * 0.12).fill(PALETTE.brass);
    g.roundRect(C * 0.06, C * 0.06, C * 3.88, C * 0.48, C * 0.1).stroke({ color: PALETTE.brassLight, width: C * 0.04 });
  }, C * 4, C * 0.6);
  bake(ASSETS.helmetLife, (g) => {
    g.moveTo(C * 0.15, C * 0.62).quadraticCurveTo(C * 0.5, C * 0.02, C * 0.85, C * 0.62).closePath().fill(PALETTE.pip);
    g.rect(C * 0.08, C * 0.6, C * 0.84, C * 0.12).fill(PALETTE.pip);
    g.circle(C * 0.5, C * 0.42, C * 0.1).fill(PALETTE.pipLamp);
  });
  bake('icon.helmet.empty', (g) => {
    g.moveTo(C * 0.15, C * 0.62).quadraticCurveTo(C * 0.5, C * 0.02, C * 0.85, C * 0.62).closePath().fill(0x8a8a8a);
    g.rect(C * 0.08, C * 0.6, C * 0.84, C * 0.12).fill(0x8a8a8a);
  });
  for (const st of ['collapsed', 'patched', 'restored'] as const) {
    bake(ASSETS.headframeSign(st), (g) => {
      g.roundRect(0, 0, C * 8, C * 2.6, C * 0.5).fill(PALETTE.uiFrame);
      g.roundRect(C * 0.2, C * 0.2, C * 7.6, C * 2.2, C * 0.4).stroke({ color: PALETTE.uiTrim, width: C * 0.12 });
    }, C * 8, C * 2.6);
    bake(ASSETS.headframeTop(st), (g) => headframe(g, st), C * 4, C * 3.6);
  }
  bake(ASSETS.floor.rope, (g) => {
    g.ellipse(C * 0.8, C * 0.7, C * 0.6, C * 0.22).fill(PALETTE.woodLight);
  }, C * 1.6, C);
  bake(ASSETS.floor.logs, (g) => {
    g.roundRect(C * 0.1, C * 0.5, C * 1.4, C * 0.3, C * 0.15).fill(PALETTE.wood);
    g.roundRect(C * 0.3, C * 0.2, C * 1.0, C * 0.3, C * 0.15).fill(PALETTE.woodLight);
  }, C * 1.6, C);
  for (let i = 0; i < ASSETS.cloudCount; i++) {
    bake(ASSETS.cloud(i), (g) => {
      g.ellipse(C * 0.5, C * 0.6, C * 0.45, C * 0.2).fill(0xffffff);
      g.circle(C * (0.35 + i * 0.05), C * 0.5, C * 0.2).fill(0xffffff);
      g.circle(C * (0.62 - i * 0.04), C * 0.48, C * 0.16).fill(0xffffff);
    });
  }
  bake(ASSETS.floor.cart, (g) => {
    g.roundRect(C * 0.15, C * 0.35, C * 0.7, C * 0.4, C * 0.06).fill(PALETTE.wood);
    g.rect(C * 0.2, C * 0.3, C * 0.6, C * 0.1).fill(PALETTE.woodLight);
    g.circle(C * 0.32, C * 0.82, C * 0.1).fill(PALETTE.uiFrame);
    g.circle(C * 0.68, C * 0.82, C * 0.1).fill(PALETTE.uiFrame);
    g.ellipse(C * 0.5, C * 0.36, C * 0.28, C * 0.1).fill(PALETTE.gold);
  });
  bake(ASSETS.floor.lanternPost, (g) => {
    g.rect(C * 0.46, C * 0.1, C * 0.08, C * 0.85).fill(PALETTE.wood);
    g.rect(C * 0.3, C * 0.1, C * 0.4, C * 0.06).fill(PALETTE.wood);
  });
  bake(ASSETS.floor.rails, (g) => {
    for (let x = 0; x < 8; x++) g.rect(x * C + C * 0.1, C * 0.3, C * 0.8, C * 0.5).fill({ color: PALETTE.wood, alpha: 0.7 });
    g.rect(0, C * 0.38, C * 8, C * 0.06).fill(PALETTE.brass);
    g.rect(0, C * 0.66, C * 8, C * 0.06).fill(PALETTE.brass);
  }, C * 8, C);

  // Solid white square for tinted fills (banners, dimmers, rings are Graphics instead)
  bake('white', (g) => {
    g.rect(0, 0, C, C).fill(0xffffff);
  });

  // ---- ART4 placeholders: particle sprites (~0.7 cell so real and placeholder emit at the same scale) and the bonus ring.
  const star = (g: Graphics, r: number, color: number, inner = 0.45): void => {
    const pts: number[] = [];
    for (let i = 0; i < 10; i++) {
      const rr = i % 2 === 0 ? r : r * inner;
      const a = (i * Math.PI) / 5 - Math.PI / 2;
      pts.push(C / 2 + Math.cos(a) * rr, C / 2 + Math.sin(a) * rr);
    }
    g.poly(pts).fill(color);
  };
  const V = ASSETS.vfx;
  bake(V.coin, (g) => {
    g.circle(C / 2, C / 2, C * 0.34).fill(PALETTE.gold);
    g.circle(C / 2, C / 2, C * 0.24).stroke({ color: PALETTE.goldShade, width: C * 0.05 });
  });
  bake(V.coinEdge, (g) => {
    g.ellipse(C / 2, C / 2, C * 0.14, C * 0.34).fill(PALETTE.goldShade);
    g.ellipse(C / 2 - C * 0.04, C / 2, C * 0.1, C * 0.3).fill(PALETTE.gold);
  });
  bake(V.sparkle, (g) => star(g, C * 0.38, 0xffe27a, 0.25));
  bake(V.sparkleSmall, (g) => star(g, C * 0.26, 0xfff4c0, 0.25));
  bake(V.starWhite, (g) => star(g, C * 0.36, 0xffffff));
  bake(V.starPink, (g) => star(g, C * 0.36, PALETTE.bonus));
  bake(V.heart, (g) => {
    g.circle(C * 0.36, C * 0.4, C * 0.18).fill(0xe0304a);
    g.circle(C * 0.64, C * 0.4, C * 0.18).fill(0xe0304a);
    g.poly([C * 0.2, C * 0.48, C * 0.8, C * 0.48, C * 0.5, C * 0.82]).fill(0xe0304a);
  });
  bake(V.shard, (g) => g.poly([C * 0.5, C * 0.14, C * 0.7, C * 0.5, C * 0.5, C * 0.86, C * 0.3, C * 0.5]).fill(0xbfe9ff));
  bake(V.glow, (g) => {
    for (let i = 8; i >= 1; i--) g.circle(C / 2, C / 2, (C * 0.5 * i) / 8).fill({ color: 0xffe9a0, alpha: 0.1 });
  });
  bake(V.dirtBig, (g) => g.poly([C * 0.2, C * 0.4, C * 0.5, C * 0.18, C * 0.82, C * 0.42, C * 0.7, C * 0.8, C * 0.3, C * 0.76]).fill(PALETTE.earthDark));
  bake(V.dirtSmall, (g) => g.poly([C * 0.35, C * 0.45, C * 0.55, C * 0.32, C * 0.68, C * 0.5, C * 0.55, C * 0.68, C * 0.38, C * 0.62]).fill(PALETTE.earthDark));
  bake(V.dustTan, (g) => {
    g.circle(C * 0.4, C * 0.55, C * 0.22).fill(0xd8c3a5);
    g.circle(C * 0.6, C * 0.45, C * 0.24).fill(0xd8c3a5);
    g.circle(C * 0.5, C * 0.62, C * 0.2).fill(0xd8c3a5);
  });
  bake(V.dustWhite, (g) => {
    g.circle(C * 0.4, C * 0.55, C * 0.22).fill(0xf4f4f4);
    g.circle(C * 0.6, C * 0.45, C * 0.24).fill(0xf4f4f4);
    g.circle(C * 0.5, C * 0.62, C * 0.2).fill(0xf4f4f4);
  });
  V.confetti.forEach((n, i) => bake(n, (g) => g.roundRect(C * 0.34, C * 0.2, C * 0.32, C * 0.6, C * 0.06).fill([0xe0304a, 0x35c9c0, 0xf2c230][i])));
  for (let i = 0; i < ASSETS.timerRingStages; i++) {
    bake(ASSETS.timerRing(i), (g) => {
      const frac = 1 - i / (ASSETS.timerRingStages - 1);
      g.circle(C / 2, C / 2, C * 0.42).stroke({ color: PALETTE.bonus, width: C * 0.1, alpha: 0.3 });
      if (frac > 0) g.moveTo(C / 2, C * 0.08).arc(C / 2, C / 2, C * 0.42, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * frac).stroke({ color: PALETTE.bonus, width: C * 0.1 });
      else g.circle(C / 2, C * 0.08, C * 0.07).fill(PALETTE.bonus);
    });
  }

  // ---- WP5 placeholders: obstacles (ART4) and the Target panel (ART5).
  const O = ASSETS.obstacle;
  O.pillar.forEach((n, k) => bake(n, (g) => {
    g.roundRect(C * 0.22, C * 0.08, C * 0.56, C * 0.86, C * 0.06).fill(0x9a9aa6);
    g.rect(C * 0.18, C * 0.2, C * 0.64, C * 0.1).fill(PALETTE.brass);
    g.rect(C * 0.18, C * 0.7, C * 0.64, C * 0.1).fill(PALETTE.brass);
    for (let i = 0; i < k; i++) g.moveTo(C * (0.3 + i * 0.2), C * 0.3).lineTo(C * (0.45 + i * 0.2), C * 0.55).lineTo(C * (0.35 + i * 0.2), C * 0.68).stroke({ color: 0x3a3a44, width: C * 0.04 });
  }));
  bake(O.rubble, (g) => {
    for (const [x, y, r] of [[0.3, 0.75, 0.16], [0.55, 0.7, 0.2], [0.75, 0.8, 0.12], [0.45, 0.85, 0.1]]) g.circle(C * x, C * y, C * r).fill(0x9a9aa6);
  });
  bake(O.boulder, (g) => {
    g.circle(C / 2, C / 2, C * 0.42).fill(0x8f8f9a);
    g.circle(C / 2, C / 2, C * 0.42).stroke({ color: PALETTE.brass, width: C * 0.05 });
  });
  O.hardDirt.forEach((n, k) => bake(n, (g) => {
    g.rect(0, 0, C, C).fill(0x6b3f22);
    for (let i = 0; i < 12; i++) g.circle(((i * 37) % 60) / 60 * C, ((i * 53) % 60) / 60 * C, C * 0.05).fill(0x8a8a8a);
    if (k === 1) g.moveTo(C * 0.2, C * 0.15).lineTo(C * 0.5, C * 0.5).lineTo(C * 0.35, C * 0.85).stroke({ color: 0x2b1a10, width: C * 0.05 });
  }));
  bake(O.lockedGem, (g) => {
    g.roundRect(C * 0.18, C * 0.18, C * 0.64, C * 0.64, C * 0.06).fill({ color: 0xbfe9ff, alpha: 0.35 });
    g.roundRect(C * 0.18, C * 0.18, C * 0.64, C * 0.64, C * 0.06).stroke({ color: PALETTE.brass, width: C * 0.06 });
  });
  bake(O.switch, (g) => {
    g.roundRect(C * 0.15, C * 0.3, C * 0.7, C * 0.45, C * 0.08).fill(0x777788);
    g.circle(C / 2, C * 0.5, C * 0.16).fill(PALETTE.brass);
  });
  bake(O.springActive, (g) => {
    g.circle(C / 2, C * 0.65, C * 0.3).stroke({ color: PALETTE.brass, width: C * 0.1 });
    star(g, C * 0.22, 0xffe27a, 0.3);
  });
  bake(O.springDormant, (g) => g.circle(C / 2, C * 0.65, C * 0.3).stroke({ color: PALETTE.brass, width: C * 0.1 }));
  bake(O.nestClosed, (g) => {
    g.circle(C / 2, C * 0.6, C * 0.38).fill(PALETTE.earthDark);
    g.circle(C / 2, C * 0.62, C * 0.22).fill(0x1a0f08);
  });
  bake(O.nestOpen, (g) => {
    g.circle(C / 2, C * 0.6, C * 0.42).fill(PALETTE.earthDark);
    g.circle(C / 2, C * 0.62, C * 0.3).fill(0x1a0f08);
    g.circle(C / 2, C * 0.62, C * 0.16).fill(PALETTE.crawler);
  });

  // ---- WP6 placeholders: lantern meter levels, charge lantern, shield bubble states.
  for (let i = 0; i < ASSETS.lanternMeterLevels; i++) {
    bake(ASSETS.lanternMeter(i), (g) => {
      const f = i / (ASSETS.lanternMeterLevels - 1);
      g.roundRect(C * 0.3, C * 0.15, C * 0.4, C * 1.2, C * 0.1).fill({ color: 0xdfe8f2, alpha: 0.5 });
      g.roundRect(C * 0.3, C * 0.15 + C * 1.2 * (1 - f), C * 0.4, C * 1.2 * f, C * 0.08).fill(0xffb43c);
      g.roundRect(C * 0.22, C * 1.35, C * 0.56, C * 0.5, C * 0.08).fill(PALETTE.brass);
    }, C, C * 2);
  }
  bake(ASSETS.lanternCharge, (g) => {
    g.roundRect(C * 0.3, C * 0.2, C * 0.4, C * 0.6, C * 0.08).fill(PALETTE.brass);
    g.circle(C / 2, C * 0.5, C * 0.14).fill(0xffd36a);
  });
  ASSETS.shield.forEach((n, i) => bake(n, (g) => {
    g.circle(C / 2, C / 2, C * 0.46).fill({ color: 0xbfe9ff, alpha: i === 2 ? 0.1 : 0.22 });
    g.circle(C / 2, C / 2, C * 0.46).stroke({ color: 0xffffff, width: C * 0.04, alpha: i === 2 ? 0.3 : 0.8 });
    if (i >= 1) g.moveTo(C * 0.3, C * 0.3).lineTo(C * 0.5, C * 0.55).lineTo(C * 0.4, C * 0.8).stroke({ color: 0xffffff, width: C * 0.03 });
  }));

  // Pose/state names resolve to their base shape so every manifest name exists in this atlas too.
  for (const [alias, base] of Object.entries(PLACEHOLDER_ALIASES)) {
    const t = map.get(base);
    if (t && !map.has(alias)) map.set(alias, t);
  }

  return new MapAtlas(map);
}

/** Small helper for HUD/label text with consistent style. */
export function makeText(text: string, size: number, opts: { bold?: boolean; color?: number; align?: 'left' | 'center' | 'right' } = {}): Text {
  return new Text({
    text,
    style: {
      fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
      fontSize: size,
      fontWeight: opts.bold ? '700' : '400',
      fill: opts.color ?? PALETTE.hudText,
      align: opts.align ?? 'left',
    },
    resolution: 2,
  });
}
